import { ExclamationCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { Tooltip } from '@mm/components';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ConfigProvider, Radio, RadioChangeEvent, Switch } from 'antd';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import pluralize from 'pluralize';
import React, { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './ContactsSync.module.css';

type Props = {
  isReadOnly: boolean;
  allConnections: ConnectionModel[];
  connection: ConnectionModel;
  name: string;
  withSwitch?: boolean;
  withSettings?: boolean;
  onSettingsClick?: () => void;
  onRadioChange?: (isAllContacts: boolean) => void;
  children: ReactNode;
};

enum AudiencesSelect {
  All = 'all',
  Audience = 'audience',
}

export const ContactsSync = ({
  isReadOnly,
  allConnections,
  connection,
  name,
  withSwitch,
  withSettings,
  onSettingsClick,
  onRadioChange,
  children,
}: Props) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const hasNativeIntegrationsFeature = featureFlags.nativeIntegrations;
  const [radioValue, setRadioValue] = useState<AudiencesSelect>(
    connection && connection?.audienceId
      ? AudiencesSelect.Audience
      : AudiencesSelect.All,
  );

  const radioAllContacts = radioValue === AudiencesSelect.All;

  const { update, deleteConnection } = useConnectionCRUDActions();
  const {
    companyName = 'Customers.ai',
    isWhitelabelCompany,
    primaryColor,
  } = useWhitelabelConfig();

  const onChange = (e: RadioChangeEvent) => {
    const { value } = e.target || {};
    setRadioValue(value);
    if (value === AudiencesSelect.All) {
      onRadioChange?.(true);
      for (let i = 1; i < allConnections.length; i += 1) {
        const connection = allConnections[i];
        if (connection.id) deleteConnection(facebookPageId, connection);
      }
      updateWebhook({ audienceId: null }, connection);
    } else {
      onRadioChange?.(false);
    }
  };

  const updateWebhook = async (
    params: Partial<ConnectionModel>,
    connection: Partial<ConnectionModel>,
  ) => {
    if (!connection) return;
    const isDeactivatingConnection = !connection.active;

    if (!isDeactivatingConnection && !hasNativeIntegrationsFeature) {
      return;
    }

    await update(facebookPageId, {
      id: connection.id,
      ...params,
    });
  };

  return (
    <div
      className={classNames(styles.box, {
        [styles.boxDisabled]: isReadOnly,
        [styles.whiteBox]: featureFlags.compliant_klaviyo_app,
      })}
    >
      <div className={styles.boxWithHelp}>
        <div className={classNames(styles.heading, styles.headingWithSwitch)}>
          Contact sync
          {radioAllContacts && withSwitch && (
            <>
              <ConfigProvider
                theme={{
                  components: {
                    Switch: {
                      colorPrimary: isWhitelabelCompany
                        ? primaryColor
                        : 'var(--color-geekblue-6)',
                    },
                  },
                }}
              >
                <Switch
                  defaultChecked={connection?.active}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  className={styles.switch}
                  onChange={active => updateWebhook({ active }, connection)}
                  disabled={!connection}
                />
              </ConfigProvider>
              {withSettings && <SettingOutlined onClick={onSettingsClick} />}
            </>
          )}
          {(connection?.webhookErrors?.length || 0) > 0 && (
            <Tooltip
              title={
                <div className={styles.errors}>
                  {connection?.webhookErrors?.map(error => (
                    <span key={uniqueId(error.errorDetails)}>
                      {error.errorDetails} -{' '}
                      {`${error.quantity} ${pluralize(
                        'error',
                        error.quantity,
                      )}`}
                    </span>
                  ))}
                </div>
              }
            >
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            </Tooltip>
          )}
        </div>
      </div>
      <Radio.Group onChange={onChange} value={radioValue}>
        <div className={styles.radioInputs}>
          <Radio
            defaultChecked={radioValue === AudiencesSelect.All}
            value={AudiencesSelect.All}
            className={styles.radio}
          >
            {`Sync all ${companyName} contacts to ${name}`}
          </Radio>
          <Radio
            defaultChecked={radioValue === AudiencesSelect.Audience}
            value={AudiencesSelect.Audience}
            className={styles.radio}
          >
            Choose an audience to sync from
          </Radio>
        </div>
      </Radio.Group>
      {children}
    </div>
  );
};
